import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Prompt } from '@app-libs/model';
import { PromptDialogComponent } from './prompt-dialog/prompt-dialog.component';
import { ChatCardComponent } from '../common/chat-card/chat-card.component';
import { PromptsApiService } from '../../services/api/prompts-api.service';

@Component({
  selector: 'app-prompts',
  imports: [ChatCardComponent],
  templateUrl: './prompts.component.html',
  styleUrls: ['./prompts.component.scss'],
})
export class PromptsComponent implements OnInit {
  prompts: Prompt[] = [];
  isLoading = false;
  displayedColumns: string[] = [
    'name',
    'category',
    'tags',
    'permission_type',
    'actions',
  ];

  constructor(
    private promptsService: PromptsApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadPrompts();
  }

  /**
   * Load all prompts from the service.
   */
  loadPrompts(): void {
    this.isLoading = true;
    this.promptsService
      .fetchAllPrompts()
      .toPromise()
      .then((prompts) => {
        this.prompts = prompts || [];
        this.isLoading = false;
      })
      .catch(() => {
        this.snackBar.open('Failed to load prompts.', 'Close', {
          duration: 3000,
        });
        this.isLoading = false;
      });
  }

  /**
   * Open dialog to create a new prompt.
   */
  createPrompt(): void {
    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '500px',
      data: { mode: 'create' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPrompts();
    });
  }

  /**
   * Open dialog to edit an existing prompt.
   * @param prompt The prompt to edit.
   */
  editPrompt(prompt: Prompt): void {
    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '500px',
      data: { mode: 'edit', prompt },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadPrompts();
    });
  }

  /**
   * Delete a prompt by its ID.
   * @param promptId ID of the prompt to delete.
   */
  deletePrompt(promptId: string): void {
    if (confirm('Are you sure you want to delete this prompt?')) {
      this.promptsService
        .deletePrompt(promptId)
        .toPromise()
        .then((success) => {
          if (success) {
            this.snackBar.open('Prompt deleted.', 'Close', { duration: 3000 });
            this.loadPrompts();
          } else {
            this.snackBar.open('Failed to delete prompt.', 'Close', {
              duration: 3000,
            });
          }
        });
    }
  }

  /**
   * Toggle prompt activation status.
   * @param prompt The prompt to activate/deactivate.
   */
  toggleActivation(prompt: Prompt): void {
    this.promptsService
      .setPromptActiveStatus(prompt.prompt_id, !prompt.is_active)
      .toPromise()
      .then((success) => {
        if (success) {
          prompt.is_active = !prompt.is_active;
          const status = prompt.is_active ? 'activated' : 'deactivated';
          this.snackBar.open(`Prompt ${status}.`, 'Close', { duration: 3000 });
        } else {
          this.snackBar.open('Failed to update status.', 'Close', {
            duration: 3000,
          });
        }
      });
  }
}
